/**
 * lib/authErrors.ts
 * ------------------------------------------------------------------
 * Translate a Firebase Auth error (or any thrown value) into a plain,
 * user-facing message. The app must NEVER surface a raw
 * "Firebase: Error (auth/…)" string to a parent or teacher — those are
 * for developers, not users. Matches on the error CODE first, then falls
 * back to substrings in the message (some SDK builds only embed the code
 * in the message text), then a safe generic default.
 * ------------------------------------------------------------------ */
export function friendlyAuthError(
  err: unknown,
  fallback = 'Something went wrong. Please try again, or contact the school.',
): string {
  const e = err as { code?: unknown; message?: unknown } | null | undefined;
  const code = String(e?.code ?? '').toLowerCase();
  const msg = String(e?.message ?? '').toLowerCase();
  const has = (frag: string) => code.includes(frag) || msg.includes(frag);

  if (has('invalid-credential') || has('invalid-login-credentials') || has('wrong-password') || has('user-not-found'))
    return 'Incorrect email or password. Please check and try again.';
  if (has('invalid-email')) return 'Please enter a valid email address.';
  if (has('missing-password')) return 'Please enter your password.';
  if (has('user-disabled')) return 'This account has been disabled. Please contact the school office.';
  if (has('too-many-requests')) return 'Too many attempts. Please wait a few minutes and try again.';
  if (has('network-request-failed')) return 'Network problem — please check your connection and try again.';
  // Reset-link continue-URL / project config problems (unauthorized domain,
  // etc.) — a parent can't fix these, so point them at the school.
  if (has('continue-uri') || has('unauthorized-continue-uri') || has('invalid-continue-uri') || has('missing-continue-uri') || has('unauthorized-domain'))
    return "We couldn't send the reset link from this page. Please ask the school to reset your password for you.";
  if (has('weak-password')) return 'Please choose a stronger password (at least 8 characters).';
  if (has('email-already-in-use')) return 'An account already exists for this email address.';
  if (has('expired-action-code')) return 'This link has expired. Please ask the school for a new one.';
  if (has('invalid-action-code')) return 'This link is invalid or has already been used. Please ask the school for a new one.';
  if (has('requires-recent-login')) return 'For your security, please sign in again to continue.';
  if (has('popup-closed-by-user') || has('cancelled-popup-request')) return 'The sign-in window closed before finishing. Please try again.';
  return fallback;
}
